import {ref, watch, computed} from "@vue/composition-api";
import store from "@/store";
import {useRouter} from "@core/utils/utils";

// Notification
import {useToast} from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {can} from "@core/libs/acl/utils";

export default function useDepartmentsList() {
    // Use toast
    const toast = useToast();
    const {route, router} = useRouter();

    const loading = ref(false)
    const saving = ref(false)
    const showCreateModal = ref(false)
    const showDeleteModal = ref(false)
    const editingUid = ref(null)
    const deleting = ref(false)
    const departmentToDelete = ref(null)
    const createError = ref('');
    const deleteError = ref('')
    const blankDepartmentData = {name: '', code: ''}
    const fieldErrors = {}

    const departmentData = ref(JSON.parse(JSON.stringify(blankDepartmentData)));

    const refDepartmentsListTable = ref(null);

    // Table Handlers
    const tableColumns = [
        {label: "Nº", key: "index", sortable: true},
        {label: "libellé", key: "name", sortable: true},
        {label: "code", key: "code", sortable: true},
        {key: "actions"},
    ];
    const perPage = ref(10);
    const currentPage = ref(1);
    const perPageOptions = [10, 25, 50, 100];
    const searchQuery = ref("");
    const sortBy = ref("created_at");
    const isSortDirDesc = ref(true);


    const departmentsList = computed(() => {
        return store.getters["department/all"];
    });

    const totalDepartments = computed(() => {
        const meta = store.getters["department/meta"];
        return meta.total ?? 0;
    });

    const dataMeta = computed(() => {
        const localItemsCount = refDepartmentsListTable.value
            ? refDepartmentsListTable.value.localItems.length
            : 0;
        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: totalDepartments.value,
        };
    });

    const refetchData = () => {
        refDepartmentsListTable.value.refresh();
    };

    watch(
        [currentPage, perPage, searchQuery],
        () => {
            refetchData();
        }
    );

    const fetchDepartmentsList = (ctx, callback) => {
        store
            .dispatch("department/fetchDepartmentsList", {
                q: searchQuery.value,
                perPage: perPage.value,
                page: currentPage.value,
                sortBy: sortBy.value,
                sortDesc: isSortDirDesc.value,
            })
            .then(() => {
                callback(departmentsList.value);
            })
    };


    const openCreateModal = () => {
        editingUid.value = null;
        departmentData.value = JSON.parse(JSON.stringify(blankDepartmentData));
        fieldErrors.value = {};
        createError.value = '';
        showCreateModal.value = true;
    }

    const openEditModal = (department) => {
        if (saving.value || deleting.value || !can('edit_department', 'Départements')) return;
        editingUid.value = departmentData.uid;
        departmentData.value = JSON.parse(JSON.stringify(department));
        fieldErrors.value = {};
        createError.value = '';
        showCreateModal.value = true;
    }

    const openDeleteModal = (department) => {
        if (saving.value || deleting.value || !can('delete_department', 'Départements')) return;
        departmentToDelete.value = department;
        deleteError.value = '';
        showDeleteModal.value = true;
    }

    const saveDepartment = async () => {
        if (saving.value) return;
        const payload = {name: departmentData.value.name.trim(), code: departmentData.value.code.trim()};
        if (!payload.name || !payload.code) return;
        saving.value = true;
        try {
            await store.dispatch(editingUid.value ? 'department/updateDepartment' : 'department/addDepartment',
                editingUid.value ? {uid: editingUid.value, ...payload} : payload);
            saving.value = false;
            showCreateModal.value = false;
            search.value = '';
            toast({
                component: ToastificationContent,
                props: {
                    title: editingUid.value ? 'Département modifié avec succès.' : 'Département créé avec succès.',
                    icon: 'CheckIcon',
                    variant: 'success'
                }
            });
            refetchData();
        } catch (error) {
            //
        } finally {
            saving.value = false;
        }
    }

    const deleteDepartment = async (departmentID) => {
        if (deleting.value || !departmentToDelete.value) return;
        deleting.value = true;
        deleteError.value = '';

        try {
            const {data, status} = await store.dispatch("department/destroyDepartment", {
                id: departmentID,
            });

            if (status !== 200) {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: data.error,
                        icon: "AlertTriangleIcon",
                        variant: "error",
                    },
                });
            } else {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: "Le département a été supprimé avec succès.",
                        icon: "AlertTriangleIcon",
                        variant: "success",
                    },
                });
                refetchData();
            }
        } catch (e) {
            //
        } finally {
            deleting.value = false;
        }
    };

    const preventDeleteClose = (ev) => {
        if (deleting.value) ev.preventDefault();
    }

    const preventSavingClose = (ev) => {
        if (saving.value) ev.preventDefault();
    }

    return {
        departmentData,
        loading,
        saving,
        deleting,
        editingUid,
        showCreateModal,
        showDeleteModal,
        openCreateModal,
        openEditModal,
        openDeleteModal,
        preventDeleteClose,
        preventSavingClose,
        saveDepartment,
        fetchDepartmentsList,
        tableColumns,
        perPage,
        currentPage,
        totalDepartments,
        dataMeta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        refDepartmentsListTable,
        refetchData,
        deleteDepartment,
        departmentToDelete
    };
}
